import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, ContestTimer, FeedbackBanner, Panel } from '@usacow/ds';
import { api, type DiagnosticItem, type DiagnosticProfile } from '../lib/api';
import { useAuth } from '../lib/auth';

const SKILL_LABEL: Record<string, string> = {
  parsing: 'Parsing',
  modeling: 'Modeling',
  algorithm_choice: 'Algorithm choice',
  execution: 'Execution',
  proof: 'Proof',
  implementation: 'Implementation',
  retention: 'Retention',
};

const TASK_LABEL: Record<string, string> = {
  entity_extraction: 'Entity extraction',
  constraint_reading: 'Constraint reading',
  pattern_selection: 'Pattern selection',
  anti_pattern: 'Anti-pattern',
  state_prediction: 'State prediction',
  edge_cases: 'Edge cases',
};

export function Diagnostic() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [items, setItems] = useState<DiagnosticItem[] | null>(null);
  const [phase, setPhase] = useState<'entry' | 'task' | 'results'>('entry');
  const [idx, setIdx] = useState(0);
  const [responses, setResponses] = useState<{ itemId: string; responseId: string }[]>([]);
  const [pick, setPick] = useState<string | null>(null);
  const [profile, setProfile] = useState<DiagnosticProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void api.getDiagnostic().then((d) => setItems(d.items));
  }, []);

  async function confirm() {
    if (!items || pick == null) return;
    const next = [...responses, { itemId: items[idx]!.id, responseId: pick }];
    setResponses(next);
    setPick(null);
    if (idx < items.length - 1) {
      setIdx(idx + 1);
    } else {
      setSubmitting(true);
      const { profile } = await api.submitDiagnostic(next);
      await refresh();
      setProfile(profile);
      setPhase('results');
      setSubmitting(false);
    }
  }

  if (!items) return <div className="route-loading">Loading diagnostic…</div>;

  if (phase === 'entry') {
    return (
      <div className="scr scr--narrow diag-entry">
        <div className="t-eyebrow">diagnostic · ~14 min · timed</div>
        <h1 className="scr-h">Reasoning assessment</h1>
        <p className="diag-lead">
          {items.length} timed microtasks measure how you read and model problems — not how many
          you&apos;ve solved. No coding. Answer from instinct.
        </p>
        <Button variant="primary" size="lg" onClick={() => setPhase('task')}>
          Begin diagnostic
        </Button>
      </div>
    );
  }

  if (phase === 'results' && profile) {
    return (
      <div className="scr scr--narrow">
        <div className="scr-head">
          <div>
            <div className="t-eyebrow">diagnostic complete</div>
            <h1 className="scr-h">Reasoning profile</h1>
          </div>
          <Badge tone={profile.placementDivision} dot>
            Placed:{' '}
            {profile.placementDivision[0]!.toUpperCase() + profile.placementDivision.slice(1)}
          </Badge>
        </div>
        {profile.topBottleneckSkill && (
          <FeedbackBanner
            state="warning"
            title={`Primary bottleneck: ${SKILL_LABEL[profile.topBottleneckSkill] ?? profile.topBottleneckSkill}.`}
            slug="your path starts here"
          >
            This is where most attempts at your level pick a doomed approach. We&apos;ll target it
            first.
          </FeedbackBanner>
        )}
        <Panel title="Reasoning skills" style={{ marginTop: 14 }}>
          <div className="diag-skills">
            {profile.skillScores.map((s) => {
              const v = s.perMille / 10;
              const color =
                v < 50 ? 'var(--incorrect)' : v < 68 ? 'var(--partial)' : 'var(--correct)';
              return (
                <div className="diag-skill" key={s.skill}>
                  <span className="diag-skill__n">{SKILL_LABEL[s.skill] ?? s.skill}</span>
                  <span className="diag-skill__bar">
                    <span style={{ width: `${v}%`, background: color }} />
                  </span>
                  <span className="diag-skill__v">{Math.round(v)}</span>
                </div>
              );
            })}
          </div>
        </Panel>
        <Button
          variant="primary"
          size="lg"
          style={{ marginTop: 16 }}
          onClick={() => navigate('/app')}
        >
          Start training
        </Button>
      </div>
    );
  }

  const item = items[idx]!;
  return (
    <div className="scr scr--narrow diag-task">
      <div className="diag-task__bar">
        <span className="t-eyebrow">
          {TASK_LABEL[item.taskType] ?? item.taskType} · {idx + 1} / {items.length}
        </span>
        <ContestTimer seconds={45} warnAt={20} critAt={8} label="this task" />
      </div>
      <div className="diag-task__progress">
        <span style={{ width: `${((idx + 1) / items.length) * 100}%` }} />
      </div>
      <p className="diag-task__q">{item.prompt}</p>
      <div className="diag-task__opts">
        {item.options.map((o) => (
          <button
            key={o.id}
            className={`diag-opt${pick === o.id ? ' is-picked' : ''}`}
            onClick={() => setPick(o.id)}
          >
            <span className="diag-opt__k">{o.id}</span>
            <span className="diag-opt__l">{o.label}</span>
          </button>
        ))}
      </div>
      <div className="diag-task__foot">
        <Button variant="primary" disabled={pick == null} loading={submitting} onClick={confirm}>
          {idx < items.length - 1 ? 'Confirm' : 'Finish'}
        </Button>
      </div>
    </div>
  );
}
